import Flatten from '@flatten-js/core';

function isSmallAngleAcute(unitVector1, unitVector2) {
  const dotProduct = unitVector1.dot(unitVector2);
  // note: dot product of unit vectors equals to the cos of the small angle they form
  return dotProduct > 0;
}

function calcPointHeight(point, reference, slopeAngle, slopeUnitVector, refHeightLine, sameHeightBehind) {
  if (slopeAngle === 0) return reference.height;
  const refPointToPointUnitVector = new Flatten.Vector(reference.point, point).normalize();
  // Imagine a plane divided in two by a perpendicular line to the slope vector
  if (isSmallAngleAcute(slopeUnitVector, refPointToPointUnitVector)) {
    // point is in the half-plane which contains the slope vector (downhill)
    const distanceToPoint = refHeightLine.distanceTo(point)[0];
    return reference.height + distanceToPoint * Math.tan(slopeAngle);
  } else {
    // point is in the half-plane which doesn't contain the slope vector (uphill)
    if (sameHeightBehind) return reference.height;
    const distanceToPoint = refHeightLine.distanceTo(point)[0];
    return reference.height - distanceToPoint * Math.tan(slopeAngle);
  }
}

/**
 * Calculates the height of one or more points based on the height of a know point and a reference vector which starts on this point.
 * A constant height line goes through the reference point, perpendicular to the reference vector.
 * A height point is calculated according to the distance to this line and the given slope angle.
 *
 * @param {Flatten.Point[]} points - Points to request height calculation for
 * @param {{point: Flatten.Point, height: number}} reference - Reference point with known height, and start of the vector slope
 * @param {Flatten.Point} slopeEndPoint - End of the vector slope. This point defines the direction, the vector length is irrelevant
 * @param {number} [slopeAngle=0] - Radiants. Positive angles make the height increase the further from ref point in ref vector direction
 * @param {boolean} [sameHeightBehind=true]
 *              - Height will be kept constant for points in the opposite direction of the vector slope, otherwise height decreases
 * @return {number[]} Array of points heights, in the same order as requested
 */
export function calcPointsHeight(points, reference, slopeEndPoint, slopeAngle = 0, sameHeightBehind = true) {
  let slopeUnitVector;
  let refHeightLine;
  if (slopeAngle !== 0) {
    if (!reference.point.equalTo(slopeEndPoint)) {
      slopeUnitVector = new Flatten.Vector(reference.point, slopeEndPoint).normalize();
      refHeightLine = new Flatten.Line(reference.point, slopeUnitVector);
    } else {
      throw Error('For slopes with angles != 0, vector start point and end point cannot be the same');
    }
  }
  const pointsHeights = [];
  for (const point of points) {
    const pointHeight = calcPointHeight(point, reference, slopeAngle, slopeUnitVector, refHeightLine, sameHeightBehind);
    pointsHeights.push(pointHeight);
  }
  return pointsHeights;
}
